import { announceCartFeedback, getCartErrorMessage, getCartSuccessMessage } from '@theme/cart-feedback';

/**
 * <s-cart-addon> adds one service variant to the cart from the cart page add-on cards
 * (blocks/cart-addon.liquid). Its "Add" button targets it with commandfor + command="--add".
 *
 * Element dataset:
 *   data-variant-id — variant to add (quantity 1).
 *   data-add-url    — routes.cart_add_url; the add POSTs to its .js endpoint (strictly additive), native form fallback without fetch.
 * While the request runs it sets [data-busy] (label swaps to "Adding…"). On success the
 * element refreshes every mounted s-cart (drawer + cart page), which re-render this card as "In your cart";
 * errors (e.g. a one-per-cart rule) are shown as a toast.
 */
class SCartAddon extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener(
      'command',
      (event) => {
        if (event.command === '--add') this.#add(event.source);
      },
      { signal: this.#ac.signal }
    );
    // Card tilt + glare (fine pointer, motion allowed). Listeners live on this untransformed element
    // so hit-testing never flickers at the tilted card's corners.
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (fine && !still) {
      const card = () => this.querySelector('.cart-card');
      this.addEventListener(
        'pointermove',
        (event) => {
          const el = card();
          if (!el) return;
          const box = el.getBoundingClientRect();
          const x = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
          const y = Math.min(Math.max((event.clientY - box.top) / box.height, 0), 1);
          this.setAttribute('data-tilting', '');
          el.style.setProperty('--ry', `${((x - 0.5) * 14).toFixed(2)}deg`);
          el.style.setProperty('--rx', `${((0.5 - y) * 10).toFixed(2)}deg`);
          el.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`);
          el.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`);
        },
        { signal: this.#ac.signal }
      );
      this.addEventListener(
        'pointerleave',
        () => {
          const el = card();
          this.removeAttribute('data-tilting');
          if (!el) return;
          for (const name of ['--rx', '--ry', '--gx', '--gy']) el.style.removeProperty(name);
        },
        { signal: this.#ac.signal }
      );
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #add(button) {
    const variantId = this.dataset.variantId;
    if (!variantId || this.hasAttribute('data-busy')) return;

    const url = this.dataset.addUrl;
    if (!url || !window.fetch) {
      this.#nativeAdd(variantId);
      return;
    }

    this.setAttribute('data-busy', '');
    button?.setAttribute('aria-busy', 'true');
    try {
      // Cart AJAX add is strictly additive: it appends this service to the existing cart and never
      // replaces the theme the buyer already picked (merchant report 2026-10-29: Standard Actions
      // updateCart from the add-on swapped Kalles for the Support Package).
      const response = await fetch(`${url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.description || data?.message;
        await announceCartFeedback(typeof message === 'string' ? message : getCartErrorMessage(null), { toast: true });
        return;
      }

      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
      await announceCartFeedback(getCartSuccessMessage([{ quantity: 1 }]));
    } catch {
      await announceCartFeedback(getCartErrorMessage(null), { toast: true });
    } finally {
      if (this.isConnected) {
        this.removeAttribute('data-busy');
        button?.removeAttribute('aria-busy');
      }
    }
  }

  #nativeAdd(variantId) {
    const url = this.dataset.addUrl;
    if (!url) return;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = url;
    for (const [name, value] of [['id', variantId], ['quantity', '1']]) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.append(input);
    }
    document.body.append(form);
    form.submit();
  }
}

if (!customElements.get('s-cart-addon')) customElements.define('s-cart-addon', SCartAddon);
